import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Loader2, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const PLAN_LABEL = {
    talent_free: { name: "Free", price: "$0", period: "forever" },
    talent_pro: { name: "Talent Pro", price: "$9.99", period: "/month" },
};

export default function TalentBilling() {
    const [sub, setSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [portalBusy, setPortalBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/billing/subscription");
            setSub(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const openPortal = async () => {
        try {
            setPortalBusy(true);
            const { data } = await api.post("/billing/customer-portal");
            window.location.assign(data.url);
        } catch (err) {
            toast.error(formatApiError(err));
            setPortalBusy(false);
        }
    };

    const planId = sub?.plan_id || "talent_free";
    const meta = PLAN_LABEL[planId] || { name: planId, price: "", period: "" };
    const isPaid = planId !== "talent_free";

    return (
        <div data-testid="talent-billing-page">
            <div className="mb-6">
                <p className="overline">Account</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Billing &amp; Subscription
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Manage your Talent Pro subscription, view invoice history, and update payment methods.
                </p>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : (
                <>
                    <div className="card-soft p-6 mb-4" data-testid="billing-current-plan">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <div className="overline">Current plan</div>
                                <div className="mt-2 flex items-baseline gap-2">
                                    <span className="font-heading font-bold text-3xl text-foreground">{meta.name}</span>
                                    {meta.price && (
                                        <span className="text-sm text-muted-foreground">
                                            {meta.price}{meta.period}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-2 flex items-center gap-2 text-xs">
                                    <Badge className="border border-border bg-muted/40 text-foreground text-[10px]">
                                        {sub?.status === "active" ? "Active" : sub?.status || "Free"}
                                    </Badge>
                                    {sub?.cancel_at_period_end && (
                                        <Badge className="border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-[10px]">
                                            Cancels at period end
                                        </Badge>
                                    )}
                                    {sub?.billing_cycle && (
                                        <span className="text-muted-foreground">Billed {sub.billing_cycle}</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row items-stretch gap-2">
                                <Link
                                    to="/pricing#talent"
                                    className="btn-outline text-sm inline-flex items-center justify-center gap-1"
                                    data-testid="billing-view-pricing"
                                >
                                    View plans <ArrowRight size={13} />
                                </Link>
                                {isPaid && (
                                    <button
                                        onClick={openPortal}
                                        disabled={portalBusy}
                                        className="btn-green text-sm inline-flex items-center justify-center gap-1 disabled:opacity-60"
                                        data-testid="billing-open-portal"
                                    >
                                        {portalBusy
                                            ? <Loader2 size={13} className="animate-spin" />
                                            : <><ExternalLink size={13} /> Manage billing</>}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {!isPaid && (
                        <div className="card-soft p-6 border-emerald-500/30 bg-emerald-500/5" data-testid="billing-upgrade-cta">
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div className="max-w-2xl">
                                    <div className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold">
                                        <Sparkles size={11} /> Unlock every AI tool
                                    </div>
                                    <h3 className="mt-3 font-heading font-medium text-xl text-foreground">
                                        Upgrade to Talent Pro
                                    </h3>
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        <b className="text-foreground">$9.99/month</b> or <b className="text-foreground">$89/year</b> (save ~26%) unlocks unlimited resume tailoring, AI cover letters, unlimited AI interview coaching, salary negotiation coach, Verified Talent Passport™ badge, and priority support.
                                    </p>
                                </div>
                                <Link to="/pricing#talent" className="btn-green text-sm inline-flex items-center gap-1" data-testid="billing-upgrade-cta-btn">
                                    Upgrade to Pro <ArrowRight size={13} />
                                </Link>
                            </div>
                        </div>
                    )}

                    <div className="card-soft p-4 mt-4 flex items-start gap-2">
                        <ShieldCheck size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-muted-foreground">
                            Cancel any time from the Customer Portal — your Pro access continues until the end of the paid billing period. No partial refunds. See the <Link to="/refund-policy" className="underline hover:text-foreground">refund policy</Link> for details.
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}
